import React, { useCallback, useMemo, useState } from 'react';
import { CustomModal } from '@/shared/components/ui/modal';
import { SearchModalContent } from '@/shared/components/ui/Search';
import { useSearchPage } from '@/shared/hooks/useSearchPage';
import ActionLinkButton from '@/shared/components/ui/ActionLinkButton';
import { SearchIcon } from 'lucide-react';
import { useCollectionStructure } from '@/features/collections/hooks/useCollectionsQuery';
import { useParams, useLocation } from 'react-router-dom';
import pageRepository from '@/features/pages/repositories/PageRepository';
import { usePagesMetadata } from '@/features/pages/hooks/usePagesMetadata';
import { useTagStore } from '@/features/tags/store/useTagStore';

export default function SearchButton({ showTitle = true }: { showTitle?: boolean }) {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  return (
    <>
      <ActionLinkButton
        Icon={SearchIcon}
        title={showTitle ? 'Search' : ''}
        onClick={() => {
          setIsModalOpen(true);
        }}
      />
      <SearchModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
      />
    </>
  );
}

export const SearchModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
}> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { collectionId } = useParams<{ collectionId: string }>();
  const [includeFullText, setIncludeFullText] = useState(false);

  const activeFilters = useTagStore(state => state.activeFilters);
  const { data: pagesMetadata } = usePagesMetadata({
    orderBy: 'updatedAt',
    filters: activeFilters,
  });
  const isInCollection = location.pathname.includes('/collection/');
  const { data: collectionStructure } = useCollectionStructure(
    isInCollection ? collectionId : undefined
  );
  // Memoized so its identity is stable while data is unchanged; a new array
  // here re-triggers useSearchPage's effect on every render.
  const currentCollectionPageMetadata = useMemo(
    () => (collectionStructure ?? []).flatMap(section => section.section.pages),
    [collectionStructure]
  );
  const data = isInCollection ? currentCollectionPageMetadata : pagesMetadata;

  const searchPages = useCallback(
    (query: string) => {
      if (includeFullText) {
        return pageRepository.searchPages(
          query,
          false,
          isInCollection ? collectionId || '' : undefined,
          true,
          data.map(page => page.id)
        );
      }

      return data.filter(p => p.title.toLowerCase().includes(query.toLowerCase()));
    },
    [collectionId, data, includeFullText, isInCollection]
  );

  const { searchQuery, searchResults, setSearchQuery, onClickPage } = useSearchPage({
    searchFn: searchPages,
    defaultResults: isInCollection ? currentCollectionPageMetadata : pagesMetadata,
    collectionId: isInCollection ? collectionId || '' : '',
  });

  const closeModal = () => {
    setSearchQuery('');
    onClose();
  };

  return (
    <CustomModal isOpen={isOpen} onClose={closeModal}>
      <SearchModalContent
        searchQuery={searchQuery}
        searchResults={searchResults}
        includeFullText={includeFullText}
        onSearchChange={setSearchQuery}
        onIncludeFullTextChange={setIncludeFullText}
        onClickPage={onClickPage}
      />
    </CustomModal>
  );
};
